import { Button } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useContext } from "react";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";
import { AppContext } from "../app/store";
import { CONSTANTS, IntlContext, useTranslate } from "../helpers";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalResults: number;
  resultsPerPage?: number;
  onPageSelect: (page: number) => void;
}

const Pagination: FunctionComponent<PaginationProps> = ({
  currentPage,
  resultsPerPage,
  totalResults,
  totalPages,
  onPageSelect,
}) => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const { dispatch } = useContext(AppContext);

  // Start and end result range for mobile view
  const startResultRange = currentPage * (resultsPerPage || 10) + 1;
  let endResultRange = startResultRange + (resultsPerPage || 10) - 1;

  if (endResultRange > totalResults) {
    endResultRange = totalResults;
  }

  function generatePageRange(
    currentPage: number,
    lastPage: number,
    delta = 2
  ): string[] {
    // creates array with base 1 index
    const range = Array(lastPage)
      .fill("")
      .map((_, index) => (index + 1).toString());

    if (lastPage <= CONSTANTS.pagination.maxNumDisplayAllPages) {
      return range;
    }

    return range.reduce((pages, page) => {
      // allow adding of first and last pages
      if (page === "1" || page === lastPage.toString()) {
        return [...pages, page.toString()];
      }

      // if within delta range add page
      if (
        Number(page) - delta <= currentPage &&
        Number(page) + delta >= currentPage
      ) {
        return [...pages, page.toString()];
      }

      // otherwise add 'gap if gap was not the last item added.
      if (pages[pages.length - 1] !== "...") {
        return [...pages, "..."];
      }

      return pages;
    }, [] as string[]);
  }

  return (
    <nav
      className="Pagination mct-hide-print"
      aria-label={fm(
        { id: "pagination.nav.label" },
        { currentPage: currentPage + 1 }
      )}
    >
      {totalPages > 1 && (
        <>
          <div className="Pagination__buttonContainer">
            {currentPage !== 0 && (
              <Button
                variation="ghost"
                className="Pagination__navButton Pagination__previous"
                aria-label={t("pagination.nav.previous_label")}
                onClick={(): void => {
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.DEFAULT,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: t("pagination.nav.previous"),
                      },
                    },
                  });
                  onPageSelect(currentPage - 1);
                }}
              >
                &lt;{" "}
                <span className="ds-u-md-display--none">
                  {t("pagination.nav.prev")}
                </span>
                <span className="ds-u-display--none ds-u-md-display--inline-block">
                  {t("pagination.nav.previous")}
                </span>
              </Button>
            )}
          </div>
          <ul className="Pagination__results" role="list">
            {generatePageRange(currentPage, totalPages).map((page, i) => (
              <li key={i} role="listitem">
                {page === "..." ? (
                  <span>...</span>
                ) : (
                  <Button
                    variation="ghost"
                    onClick={(): void => {
                      onPageSelect(Number(page) - 1);
                      dispatch({
                        type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                        settings: {
                          button: {
                            buttonStyle: AnalyticsButtonStyle.DEFAULT,
                            buttonType: AnalyticsButtonType.BUTTON,
                            text: page,
                          },
                        },
                      });
                    }}
                    aria-label={
                      Number(page) - 1 == currentPage
                        ? fm(
                            { id: "pagination.nav.current_page" },
                            { currentPage: currentPage + 1 }
                          )
                        : fm(
                            { id: "pagination.nav.go_to_page" },
                            { pageNumber: page }
                          )
                    }
                    aria-current={
                      Number(page) - 1 === currentPage ? "page" : undefined
                    }
                    className={`Pagination__pageButton ${
                      Number(page) - 1 == currentPage
                        ? "Pagination__pageButton--active"
                        : ""
                    }`}
                  >
                    {page}
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <div className="Pagination__mobileResults">
            {startResultRange}-{endResultRange} {t("of")} {totalResults}
          </div>
          <div className="Pagination__buttonContainer">
            {currentPage !== totalPages - 1 && (
              <Button
                variation="ghost"
                className="Pagination__navButton Pagination__next"
                aria-label={t("pagination.nav.next_label")}
                onClick={(): void => {
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.DEFAULT,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: t("pagination.nav.next"),
                      },
                    },
                  });
                  onPageSelect(currentPage + 1);
                }}
              >
                {t("pagination.nav.next")} &gt;
              </Button>
            )}
          </div>
        </>
      )}
    </nav>
  );
};

export default Pagination;
